import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../fetchInterceptor';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Shield, Users, UserCheck, Lock, Unlock, Check } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import InfoTooltip from './InfoTooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Renders an "Access" panel for a single contact. Lets the admin/creator switch
 * between two modes:
 *   - "all":        everyone in the workspace can see this contact
 *   - "restricted": only the listed users (plus workspace owners/admins)
 */
export default function ContactAccessPanel({ contactId, contactName }) {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;
  const [access, setAccess] = useState(null);
  const [members, setMembers] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [draftIds, setDraftIds] = useState(new Set());

  const fetchAccess = useCallback(async () => {
    if (!contactId) return;
    const r = await apiFetch(`${API_URL}/api/contacts/${contactId}/access`);
    if (r.ok) {
      const data = await r.json();
      setAccess(data);
      setDraftIds(new Set(data.allowed_user_ids || []));
    }
  }, [contactId]);

  const fetchMembers = useCallback(async () => {
    if (!wsId) return;
    const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/access-overview`);
    if (r.ok) {
      const data = await r.json();
      setMembers(data.members || []);
    } else if (r.status === 403) {
      // Non-admin: fall back to a regular members list endpoint
      const r2 = await apiFetch(`${API_URL}/api/workspaces/${wsId}/members`);
      if (r2.ok) setMembers(await r2.json());
    }
  }, [wsId]);

  useEffect(() => { fetchAccess(); }, [fetchAccess]);
  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const setMode = async (mode, allowed_user_ids = []) => {
    const r = await apiFetch(`${API_URL}/api/contacts/${contactId}/access`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode, allowed_user_ids }),
    });
    if (r.ok) {
      const data = await r.json();
      setAccess({ ...access, access_mode: data.access_mode, allowed_user_ids: data.allowed_user_ids });
      toast.success(mode === 'all' ? 'Now visible to all workspace members' : `Restricted to ${data.allowed_user_ids.length} user(s)`);
      fetchAccess();
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || 'Failed to update access');
    }
  };

  const saveRestriction = async () => {
    const ids = Array.from(draftIds);
    if (ids.length === 0) {
      toast.error('Pick at least one user');
      return;
    }
    await setMode('restricted', ids);
    setShowPicker(false);
  };

  const mode = access?.access_mode || 'all';
  const allowedCount = (access?.allowed_user_ids || []).length;
  const allowedSet = new Set(access?.allowed_user_ids || []);

  return (
    <Card data-testid="contact-access-panel">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-emerald-500" />
          <h3 className="font-semibold text-base">Access</h3>
          <InfoTooltip title="Contact Access" testId="info-contact-access" side="bottom">
            <p>Decide who in your workspace can see this contact.</p>
            <ul className="list-disc pl-4 space-y-0.5">
              <li><strong>All members</strong> — everyone with workspace access can view.</li>
              <li><strong>Restricted</strong> — only the users you list (plus workspace owners/admins).</li>
            </ul>
            <p className="text-[10px] italic">Workspace owners &amp; admins always see every contact.</p>
          </InfoTooltip>
        </div>

        {!access ? (
          <p className="text-sm text-muted-foreground" data-testid="access-loading">Loading access settings…</p>
        ) : (
        <>
        <div className={cn(
          'rounded-md border p-3 flex items-start gap-3',
          mode === 'all' ? 'bg-emerald-50/40 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-900/40' : 'bg-amber-50/40 border-amber-200 dark:bg-amber-950/20 dark:border-amber-900/40',
        )}>
          {mode === 'all' ? <Unlock className="w-4 h-4 text-emerald-600 mt-0.5" /> : <Lock className="w-4 h-4 text-amber-600 mt-0.5" />}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium" data-testid="access-mode-banner">
              {mode === 'all'
                ? 'Visible to all workspace members'
                : `Restricted — ${allowedCount} user${allowedCount === 1 ? '' : 's'} can access`}
            </div>
            <p className="text-xs text-muted-foreground">
              {mode === 'all'
                ? 'Any teammate with workspace access can view, edit, and file documents for this contact.'
                : 'Only the users listed below (plus workspace owners/admins) can see this contact, its folder, and its invoices/bills.'}
            </p>
          </div>
        </div>

        {mode === 'restricted' && allowedCount > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {(access.allowed_members || []).map(m => (
              <Badge key={m.user_id} variant="secondary" className="text-[11px] py-0.5 pl-1.5 pr-2 flex items-center gap-1.5">
                <UserCheck className="w-3 h-3" />
                {m.name || m.email || m.user_id}
              </Badge>
            ))}
          </div>
        )}

        <div className="flex gap-2 flex-wrap pt-1">
          <Button
            size="sm" variant={mode === 'all' ? 'default' : 'outline'}
            onClick={() => setMode('all')}
            className={mode === 'all' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
            data-testid="grant-all-btn"
          >
            <Users className="w-3.5 h-3.5 mr-1.5" /> Grant all members
          </Button>
          <Button
            size="sm" variant={mode === 'restricted' ? 'default' : 'outline'}
            onClick={() => setShowPicker(true)}
            className={mode === 'restricted' ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
            data-testid="restrict-btn"
          >
            <Lock className="w-3.5 h-3.5 mr-1.5" /> {mode === 'restricted' ? 'Edit access list…' : 'Restrict to specific users…'}
          </Button>
        </div>
        </>
        )}

        {/* Restriction picker modal */}
        {showPicker && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center" data-testid="access-picker-modal">
            <Card className="w-full max-w-md mx-4">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-amber-500" />
                  <h3 className="font-semibold text-lg">Restrict access</h3>
                </div>
                <p className="text-xs text-muted-foreground">
                  Pick who should be able to see <strong>{contactName}</strong>. Owners &amp; admins always have access.
                </p>
                <div className="max-h-64 overflow-y-auto bg-muted/40 rounded p-2 space-y-1">
                  {members.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground italic px-1">No workspace members loaded.</p>
                  ) : members.map(m => {
                    const checked = draftIds.has(m.user_id);
                    const isAdmin = ['owner', 'admin'].includes((m.role || '').toLowerCase());
                    return (
                      <label
                        key={m.user_id}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm"
                        data-testid={`access-pick-${m.user_id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked || isAdmin}
                          disabled={isAdmin}
                          onChange={() => {
                            setDraftIds(prev => {
                              const next = new Set(prev);
                              if (next.has(m.user_id)) next.delete(m.user_id);
                              else next.add(m.user_id);
                              return next;
                            });
                          }}
                          className="h-4 w-4"
                        />
                        <span className="flex-1 truncate">{m.name || m.email || m.user_id}</span>
                        {isAdmin && (
                          <Badge variant="outline" className="text-[9px] flex items-center gap-0.5">
                            <Check className="w-2.5 h-2.5" /> always
                          </Badge>
                        )}
                        {!isAdmin && allowedSet.has(m.user_id) && (
                          <Badge variant="secondary" className="text-[9px]">current</Badge>
                        )}
                      </label>
                    );
                  })}
                </div>
                <div className="flex justify-between items-center pt-2">
                  <Button
                    size="sm" variant="ghost" className="text-xs"
                    onClick={() => setDraftIds(new Set(members.filter(m => !['owner','admin'].includes((m.role||'').toLowerCase())).map(m => m.user_id)))}
                    data-testid="pick-all-members-btn"
                  >Pick everyone</Button>
                  <div className="flex gap-2">
                    <Button variant="ghost" onClick={() => setShowPicker(false)}>Cancel</Button>
                    <Button onClick={saveRestriction} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-restriction-btn">
                      Save restriction
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
